import { getAdminMenus, isAdminMenuOn, type AdminMenus } from './adminMenus';

/**
 * Which admin menu id owns each URL in this panel.
 *
 * `adminMenus.ts` says whether a row is painted. This says where that row goes,
 * which is what lets the router refuse a page the super admin took away —
 * otherwise hiding a row only removed the button and the page still answered to
 * anyone typing the URL or holding a bookmark.
 *
 * Prefix-matched, longest first, so `/members/:id/sub/:memberid/bet-history`
 * resolves through its `/members` parent. A path no entry owns has no switch
 * behind it and stays open: closing a URL the panel gives no way to restore
 * would be a change nobody could undo.
 *
 * The ids MUST stay in step with the backend's `brand-admin-menus.ts` and the
 * `menu` keys in `EnhancedSidebar.tsx`.
 */
type RouteEntry = readonly [path: string, menu: string, parent?: string];

const ROUTE_MENUS: ReadonlyArray<RouteEntry> = [
  // More specific paths first — the table is sorted by length below, but
  // keeping related rows together reads better than a pre-sorted list.
  ['/new-dashboard', 'newDashboard'],
  ['/dashboard', 'dashboard'],
  ['/users', 'userManagement'],
  ['/members', 'downline'],
  ['/risk', 'riskManagement'],

  ['/bet-list', 'betList', 'myReports'],
  ['/casino-bet-list', 'casinoBetList', 'myReports'],
  ['/pl-by-downline', 'plByDownline', 'myReports'],
  ['/pl-by-event', 'plByEvent', 'myReports'],
  ['/pl-by-player', 'plByPlayer', 'myReports'],
  ['/casinopl-by-downline', 'casinoPlByDownline', 'myReports'],
  ['/casinopl-by-player', 'casinoPlByPlayer', 'myReports'],
  ['/business-reports', 'businessReport', 'myReports'],
  ['/inactive-members', 'inactiveMembers', 'myReports'],

  ['/event-management', 'events', 'eventManagement'],
  ['/bet-setting', 'betConfig', 'eventManagement'],
  ['/casino-management', 'casinoControl'],

  ['/requests', 'depositWithdrawRequest', 'bankingManagement'],
  ['/manage-account', 'manageAccounts', 'bankingManagement'],
  ['/payment', 'paymentSettings', 'bankingManagement'],
  ['/fd', 'fixedDeposit', 'bankingManagement'],

  ['/content-management', 'contentManagement'],
  ['/brands', 'whiteLabel'],
  ['/app-management', 'appManagement'],

  ['/affiliate-dashboard', 'affiliateDashboard', 'affiliateManagement'],
  ['/affiliate-req', 'affiliateRequests', 'affiliateManagement'],
  ['/affiliate-list', 'affiliateListing', 'affiliateManagement'],
  ['/affiliate-comission-report', 'commissionReport', 'affiliateManagement'],
  ['/affiliate-setting', 'commissionSettings', 'affiliateManagement'],

  ['/export', 'exportReportHistory'],
  ['/account-statement', 'accountStatement'],
  ['/settings', 'changePassword'],
  ['/activity-logs', 'activityLog'],
];

/** Longest prefix wins, so `/affiliate-list` never shadows `/affiliate-listing`. */
const SORTED = [...ROUTE_MENUS].sort((a, b) => b[0].length - a[0].length);

function entryFor(pathname: string): RouteEntry | undefined {
  const path = pathname.toLowerCase().replace(/\/+$/, '') || '/';
  return SORTED.find(([prefix]) => path === prefix || path.startsWith(`${prefix}/`));
}

export function adminMenuForRoute(pathname: string): string | undefined {
  return entryFor(pathname)?.[1];
}

/**
 * Whether the router should refuse this URL for the signed-in owner.
 *
 * Both the row's own id AND its branch parent are checked. Switching off
 * "Banking Management" takes the whole branch out of the rail, but each child
 * keeps its own `true` in the stored map — so testing the child alone would
 * leave `/payment` answering to anyone who typed it. Enforcing the parent here
 * means the rule holds whatever the super-admin UI happens to write.
 */
export function isAdminRouteHidden(
  pathname: string,
  menus: AdminMenus = getAdminMenus(),
): boolean {
  const entry = entryFor(pathname);
  if (!entry) return false;
  const [, menu, parent] = entry;
  return !isAdminMenuOn(menu, menus) || !isAdminMenuOn(parent, menus);
}
